/**
 * Collection sort menu, including the entry point for the price-range dialog.
 */
(function () {
  const INIT_ATTR = 'data-collection-sort-init';
  const OPEN_CLASS = 'is-open';
  const MOBILE_QUERY = '(max-width: 749px)';

  function navigateWithSort(value, dropdown) {
    const url = new URL(window.location.href);
    const priceFilter = dropdown
      ?.closest('[data-collection-sort-bar]')
      ?.querySelector('[data-collection-price-filter]');
    const minimumPriceParam = priceFilter?.dataset.priceParamMin || 'filter.v.price.gte';
    const maximumPriceParam = priceFilter?.dataset.priceParamMax || 'filter.v.price.lte';

    url.searchParams.set('sort_by', value);
    url.searchParams.delete(minimumPriceParam);
    url.searchParams.delete(maximumPriceParam);
    url.searchParams.delete('page');

    const controller =
      window.CollectionController?.instance || window.CollectionController?.init();

    if (controller) {
      return controller.navigate(url.pathname + url.search, {
        updateToolbar: true,
        updateHeader: true,
        source: 'sort',
      });
    }

    window.location.assign(url.toString());
    return null;
  }

  function getOptions(dropdown) {
    return Array.from(
      dropdown.querySelectorAll('[data-collection-sort-option], [data-price-modal-trigger]')
    ).filter((button) => !button.disabled && !button.closest('li')?.hidden);
  }

  function closeDropdown(dropdown, returnFocus = false) {
    const trigger = dropdown.querySelector('[data-collection-sort-trigger]');
    const menu = dropdown.querySelector('[data-collection-sort-menu]');
    const backdrop = dropdown.querySelector('[data-collection-sort-backdrop]');

    dropdown.classList.remove(OPEN_CLASS);
    trigger?.setAttribute('aria-expanded', 'false');
    if (menu) {
      menu.hidden = true;
    }
    if (backdrop) backdrop.hidden = true;
    if (!document.querySelector(`[data-collection-sort-dropdown].${OPEN_CLASS}`)) {
      document.body.classList.remove('collection-sort-modal-open');
    }
    if (returnFocus) trigger?.focus();
  }

  function openDropdown(dropdown, focusFirst = false) {
    document.querySelectorAll(`[data-collection-sort-dropdown].${OPEN_CLASS}`).forEach((openItem) => {
      if (openItem !== dropdown) closeDropdown(openItem);
    });

    const trigger = dropdown.querySelector('[data-collection-sort-trigger]');
    const menu = dropdown.querySelector('[data-collection-sort-menu]');
    const backdrop = dropdown.querySelector('[data-collection-sort-backdrop]');
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    dropdown.classList.add(OPEN_CLASS);
    trigger?.setAttribute('aria-expanded', 'true');
    if (menu) {
      menu.hidden = false;
    }
    if (isMobile) {
      if (backdrop) backdrop.hidden = false;
      document.body.classList.add('collection-sort-modal-open');
    }

    if (focusFirst || isMobile) {
      const active = dropdown.querySelector('.collection-sort-dropdown__option.is-active');
      (active || getOptions(dropdown)[0])?.focus();
    }
  }

  function setActiveSort(dropdown, optionButton) {
    getOptions(dropdown).forEach((button) => {
      const isActive = button === optionButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', String(isActive));
    });

    const bar = dropdown.closest('[data-collection-sort-bar]');
    const value = bar?.querySelector('[data-collection-sort-value]');
    const trigger = bar?.querySelector('[data-collection-sort-trigger]');
    const label = optionButton.textContent.trim();
    if (value) value.textContent = label;
    if (trigger) trigger.setAttribute('aria-label', `Sort by, ${label}`);
  }

  function moveFocus(dropdown, current, direction) {
    const options = getOptions(dropdown);
    const currentIndex = options.indexOf(current);
    if (!options.length) return;

    let nextIndex;
    if (direction === 'first') nextIndex = 0;
    else if (direction === 'last') nextIndex = options.length - 1;
    else nextIndex = (Math.max(currentIndex, 0) + direction + options.length) % options.length;
    options[nextIndex]?.focus();
  }

  function bindCollectionSortDropdown(dropdown) {
    if (dropdown.getAttribute(INIT_ATTR) === 'true') return;
    dropdown.setAttribute(INIT_ATTR, 'true');

    const trigger = dropdown.querySelector('[data-collection-sort-trigger]');
    const menu = dropdown.querySelector('[data-collection-sort-menu]');
    const backdrop = dropdown.querySelector('[data-collection-sort-backdrop]');
    const closeButton = dropdown.querySelector('[data-collection-sort-close]');
    const bar = dropdown.closest('[data-collection-sort-bar]');
    if (!trigger || !menu) return;

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (dropdown.classList.contains(OPEN_CLASS)) closeDropdown(dropdown);
      else openDropdown(dropdown);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      openDropdown(dropdown, true);
      if (event.key === 'ArrowUp') moveFocus(dropdown, getOptions(dropdown)[0], 'last');
    });

    menu.addEventListener('keydown', (event) => {
      const option = event.target.closest('[data-collection-sort-option], [data-price-modal-trigger]');
      if (event.key === 'Tab' && window.matchMedia(MOBILE_QUERY).matches) {
        const focusable = [closeButton, ...getOptions(dropdown)].filter(Boolean);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && event.target === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && event.target === last) {
          event.preventDefault();
          first?.focus();
        }
        return;
      }
      if (!option) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveFocus(dropdown, option, 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveFocus(dropdown, option, -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        moveFocus(dropdown, option, 'first');
      } else if (event.key === 'End') {
        event.preventDefault();
        moveFocus(dropdown, option, 'last');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown(dropdown, true);
      }
    });

    menu.querySelectorAll('[data-collection-sort-option]').forEach((optionButton) => {
      optionButton.addEventListener('click', (event) => {
        event.preventDefault();
        const value = optionButton.dataset.value;
        if (!value) return;

        if (optionButton.classList.contains('is-active')) {
          closeDropdown(dropdown, true);
          return;
        }

        setActiveSort(dropdown, optionButton);
        closeDropdown(dropdown);
        bar?.classList.add('is-loading');
        if (bar) bar.dataset.pendingSort = value;

        const navigation = navigateWithSort(value, dropdown);
        if (navigation && typeof navigation.finally === 'function') {
          navigation.finally(() => {
            if (bar) {
              bar.dataset.currentSort = value;
              delete bar.dataset.pendingSort;
              bar.classList.remove('is-loading');
            }
          });
        }
      });
    });

    menu.querySelector('[data-price-modal-trigger]')?.addEventListener('click', (event) => {
      event.preventDefault();
      closeDropdown(dropdown);
      window.openCollectionPriceDialog?.(bar?.querySelector('[data-collection-price-filter]'));
    });

    backdrop?.addEventListener('click', () => closeDropdown(dropdown, true));
    closeButton?.addEventListener('click', () => closeDropdown(dropdown, true));
  }

  function bindGlobalCloseHandlers() {
    if (document.documentElement.getAttribute('data-collection-sort-global') === 'true') return;
    document.documentElement.setAttribute('data-collection-sort-global', 'true');

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-collection-sort-dropdown]')) return;
      document.querySelectorAll(`[data-collection-sort-dropdown].${OPEN_CLASS}`).forEach((dropdown) => {
        closeDropdown(dropdown);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll(`[data-collection-sort-dropdown].${OPEN_CLASS}`).forEach((dropdown) => {
        closeDropdown(dropdown, true);
      });
    });
  }

  function initCollectionSort(root = document) {
    bindGlobalCloseHandlers();
    root.querySelectorAll('[data-collection-sort-dropdown]').forEach(bindCollectionSortDropdown);
  }

  window.initCollectionSort = initCollectionSort;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCollectionSort());
  } else {
    initCollectionSort();
  }

  document.addEventListener('shopify:section:load', (event) => initCollectionSort(event.target));
  document.addEventListener('collection-grid-updated', () => initCollectionSort(document));
})();
